import { User } from "firebase/auth";
import { useContext, useEffect, useMemo, useState } from 'react';
import {
    collection,
    collectionGroup,
    doc,
    getDoc,
    or,
    query,
    setDoc,
    where,
} from 'firebase/firestore';
import { and } from "@firebase/firestore";
import {
    useFirebaseAuth,
    FirebaseRefs,
    useFirebaseRefs,
    useFirebaseUserInfoFromIndexedDB,
    useDoc,
    useCollection,
    useMemoizedQuery,
} from './firebase';
import {
    ChangeLog,
    changeLogConverter,
    firebaseRecordToUserClass,
    Guide,
    HomeData,
    Place,
    placeConverter,
    Trip,
    tripConverter,
    guideConverter,
    UserClass,
    guideSlugClassConverter,
} from './interfaces';
import { LoggedInUserData, PrivateUserData, PublicUserData, UserData } from "./userdata";
import { HomeContext } from "./context";
import { getLogger, setNeedsFirebaseAuthTimeoutHack } from "./logger";
import { executeQueriesAgainstCacheThenServer } from './placeFunctions';
import { splitArray } from "../../shared/collections";

const logger = getLogger('hooks');

// Private-user-data prefs cached in localStorage, keyed by uid, so a relaunch can render
// preference-dependent UI (e.g. the trip Plans calendar/list view) immediately instead of
// blocking on the private-user-data Firestore read. The cached values are reconciled with the
// authoritative doc as soon as it resolves. Only the (small, serializable, non-sensitive) prefs
// are cached — not the privateDataLoaded status flag, which is set true when serving the cache.
type CachedPrivatePrefs = Omit<PrivateUserData, 'privateDataLoaded'>;
const PRIVATE_PREFS_KEY_PREFIX = 'privateUserData:';

function readCachedPrivatePrefs(uid: string | undefined): CachedPrivatePrefs | undefined {
    if (!uid || typeof window === 'undefined') {
        return undefined;
    }
    try {
        const raw = window.localStorage.getItem(`${PRIVATE_PREFS_KEY_PREFIX}${uid}`);
        return raw ? JSON.parse(raw) as CachedPrivatePrefs : undefined;
    } catch {
        return undefined;
    }
}

function writeCachedPrivatePrefs(uid: string, prefs: CachedPrivatePrefs): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(`${PRIVATE_PREFS_KEY_PREFIX}${uid}`, JSON.stringify(prefs));
    } catch {
        /* ignore quota / serialization errors */
    }
}

/**
 * How long to wait for Firebase Auth to report a state before assuming it never will. Generous: a
 * cold session restore on a slow connection is a few seconds, and this only needs to catch a stall.
 */
const AUTH_RESOLUTION_TIMEOUT_MS = 8000;

export function useUserDataContextState(): UserData {
    const firebaseRefs = useFirebaseRefs();

    const auth = useFirebaseAuth();

    const [ authStateUser, setAuthStateUser ] = useState<User | null>();
    const [ isLoadingAuthState, setIsLoadingAuthState ] = useState(true);

    const indexedDBInfo = useFirebaseUserInfoFromIndexedDB();

    useEffect(() => {
        if (auth) {
            auth.beforeAuthStateChanged(() => {
                setIsLoadingAuthState(true);
            });
            auth.onAuthStateChanged(user => {
                logger.log(`Auth state changed. User id=${user?.uid}`);
                setAuthStateUser(user);
                setIsLoadingAuthState(false);
            });
        } else {
            setIsLoadingAuthState(true);
        }
    }, [ auth ]);

    // Watchdog: onAuthStateChanged is not guaranteed to fire. It stalls indefinitely whenever
    // Firebase Auth cannot open its own IndexedDB (firebaseLocalStorageDb) -- most often because
    // another tab on this origin holds a connection, which blocks the open with no error and no
    // network traffic. Without this, the app sits on the auth gate forever: no content, and no login
    // screen either, because 'logged-out' is only reachable once loading clears.
    //
    // Falling back to logged-out is recoverable, not destructive: a later onAuthStateChanged still
    // lands and promotes the user. Showing a login button beats an infinite spinner.
    useEffect(() => {
        if (!isLoadingAuthState) {
            return undefined;
        }
        const timer = setTimeout(() => {
            logger.warn(`Auth state did not resolve within ${AUTH_RESOLUTION_TIMEOUT_MS}ms.`
                + ' Falling back to logged-out so the UI is usable.'
                + ' Firebase Auth is most likely blocked opening its IndexedDB by another tab.');
            setIsLoadingAuthState(false);
        }, AUTH_RESOLUTION_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [ isLoadingAuthState ]);

    const publicUserDataFromDatabase = useDoc(
        authStateUser?.uid ? doc(firebaseRefs.firestore, 'users', authStateUser.uid) : undefined,
        'useUserData:public-user-data'
    );
    const publicUserData: PublicUserData | undefined = useMemo(
        () => {
            if (authStateUser) {
                if (publicUserDataFromDatabase.isLoading) {
                    // Deliberately no username/displayName/email here: this branch cannot know the
                    // user's username yet, and username generation treats a missing username as
                    // "this user has none" and mints a new one. Supplying a name or email while
                    // leaving username undefined therefore let a returning user's handle be
                    // rewritten (klucas -> klucas4821) if a guide was saved or published during
                    // this window. With all three absent, getUsername returns null and writes
                    // nothing until the record below resolves.
                    return {
                        isLoggedIn: true,
                        status: 'logged-in',
                        firebaseUserId: authStateUser.uid,
                        publicDataLoaded: false,
                    };
                } else {
                    const data = publicUserDataFromDatabase.result;
                    return {
                        isLoggedIn: true,
                        status: 'logged-in',
                        username: data?.username,
                        firebaseUserId: authStateUser.uid,
                        // Prefer the users-doc values, but fall back to the auth record so callers
                        // (the display-name onboarding prompt, username generation) see them even
                        // before/without a user doc. A provider sign-in (e.g. Google) supplies a
                        // display name on the auth record, and the onUserCreation trigger copies it
                        // into the doc — but that trigger is asynchronous, and historically could
                        // fail to run at all, so without this fallback such a user was prompted to
                        // choose a display name they already had.
                        displayName: data?.displayName ?? authStateUser.displayName,
                        email: data?.email ?? authStateUser.email,
                        devTools: data?.devTools ?? null,
                        photoURL: data?.photoURL,
                        publicDataLoaded: true,
                    };
                }
            } else if (isLoadingAuthState) {
                if (!indexedDBInfo.isLoading && indexedDBInfo.firebaseUserId) {
                    return {
                        isLoggedIn: false,
                        status: 'cached-data-available',
                        firebaseUserId: indexedDBInfo.firebaseUserId,
                    };
                } else {
                    return {
                        isLoggedIn: false,
                        status: 'unknown',
                    };
                }
            } else {
                return {
                    isLoggedIn: false,
                    status: 'logged-out',
                };
            }
        },
        [ indexedDBInfo.firebaseUserId, indexedDBInfo.isLoading, publicUserDataFromDatabase, authStateUser, isLoadingAuthState ]
    );

    const privateUserDataDoc = useDoc(
        authStateUser?.uid ? privateDocRef(firebaseRefs, authStateUser.uid) : undefined,
        'useUserData:private-user-data'
    );
    // Cached prefs for the live or last-known uid, read synchronously so we can serve them while
    // the Firestore doc is still loading rather than gating the UI on it.
    const prefsUid = authStateUser?.uid ?? indexedDBInfo.firebaseUserId;
    const cachedPrivatePrefs = useMemo(() => readCachedPrivatePrefs(prefsUid), [ prefsUid ]);

    const privateUserData: PrivateUserData = useMemo(
        () => {
            if (authStateUser && privateUserDataDoc?.isLoading) {
                // The doc hasn't resolved yet. Serve cached prefs optimistically (as loaded) so
                // preference-dependent UI renders immediately; the authoritative values replace
                // these as soon as the doc resolves below.
                if (cachedPrivatePrefs) {
                    return { ...cachedPrivatePrefs, privateDataLoaded: true };
                }
                return { privateDataLoaded: false };
            } else {
                // Anonymous sessions default to the calendar view. A logged-in user keeps their
                // saved preference (undefined -> list view), so a missing private-user-data doc
                // does not force the calendar view onto preference-less logged-in users.
                const tripView = authStateUser ? privateUserDataDoc.result?.tripView : 'calendar';

                return {
                    countryTracking: privateUserDataDoc.result?.countryTracking === 'enabled' ? 'enabled' : 'disabled',
                    searchProviders: privateUserDataDoc.result?.searchProviders ?? undefined,
                    tripView,
                    displayNamePromptDismissed: privateUserDataDoc.result?.displayNamePromptDismissed === true,
                    privateDataLoaded: true,
                };
            }
        },
        [ authStateUser, privateUserDataDoc, cachedPrivatePrefs ]
    );

    // Write-through: persist the authoritative prefs once the doc resolves, so the next launch can
    // render from cache. Keyed by uid so account-switching never serves another user's prefs.
    useEffect(() => {
        if (authStateUser?.uid && !privateUserDataDoc.isLoading) {
            writeCachedPrivatePrefs(authStateUser.uid, {
                countryTracking: privateUserDataDoc.result?.countryTracking === 'enabled' ? 'enabled' : 'disabled',
                searchProviders: privateUserDataDoc.result?.searchProviders ?? undefined,
                tripView: privateUserDataDoc.result?.tripView,
                displayNamePromptDismissed: privateUserDataDoc.result?.displayNamePromptDismissed === true,
            });
        }
    }, [ authStateUser, privateUserDataDoc ]);

    useEffect(() => {
        if (authStateUser) {
            setNeedsFirebaseAuthTimeoutHack(false); // if we've gotten this far, then the Firebase Auth initialization woes are behind us
        }
    }, [ authStateUser ]);

    return useMemo(
        () => ({
            ...publicUserData,
            ...privateUserData,
        }),
        [ privateUserData, publicUserData ]
    );
}

function privateDocRef(firebaseRefs: FirebaseRefs, userId: string) {
    return doc(firebaseRefs.firestore, 'private-user-data', userId);
}

export async function mergePrivateUserData(firebaseRefs: FirebaseRefs, userData: LoggedInUserData, fields: Partial<PrivateUserData>) {
    await mergePrivateUserDataByUid(firebaseRefs, userData.firebaseUserId, fields);
}

// Same as mergePrivateUserData, for callers that hold only a uid rather than the full user record.
export async function mergePrivateUserDataByUid(
    firebaseRefs: FirebaseRefs,
    uid: string,
    fields: Partial<PrivateUserData>
) {
    await setDoc(privateDocRef(firebaseRefs, uid), fields, { merge: true });
}

export function useUserByIdWithStatus(userid: string | undefined): { user: UserClass | null, isLoading: boolean } {
    const firebaseRefs = useFirebaseRefs();
    const docRef = userid ? doc(firebaseRefs.firestore, 'users', userid) : undefined;
    const { result, isLoading } = useDoc(docRef, 'useUserById');

    const user = useMemo(() => firebaseRecordToUserClass(result), [ result ]);
    return { user, isLoading: !!userid && isLoading };
}

export function useUserById(userid: string | undefined): UserClass | null {
    return useUserByIdWithStatus(userid).user;
}

export function useUserIdByUsernameWithStatus(username: string | undefined): { userId: string | undefined, isLoading: boolean } {
    const firebaseRefs = useFirebaseRefs();
    const docRef = username ? doc(firebaseRefs.firestore, 'usernames', username) : undefined;
    const { result, isLoading } = useDoc(docRef, 'useUserIdByUsername');

    const userId = useMemo(() => (result ? result.uid : undefined), [ result ]);
    return { userId, isLoading: !!username && isLoading };
}

export function useUserIdByUsername(username: string | undefined): string | undefined {
    return useUserIdByUsernameWithStatus(username).userId;
}

export function useGuideIdByUserIdAndSlugWithStatus(userId: string | undefined, slug: string | undefined): { guideId: string | undefined, isLoading: boolean } {
    const firebaseRefs = useFirebaseRefs();
    const hasInputs = !!userId && !!slug;
    const docRef = hasInputs ? doc(firebaseRefs.firestore, 'users', userId!, 'guideSlugs', slug!) : undefined;
    const { result, isLoading } = useDoc(docRef, 'useGuideIdByUserIdAndSlug');

    const guideId = useMemo(() => (result ? result.guideId : undefined), [ result ]);
    return { guideId, isLoading: hasInputs && isLoading };
}

export function useGuideIdByUserIdAndSlug(userId: string | undefined, slug: string | undefined): string | undefined {
    return useGuideIdByUserIdAndSlugWithStatus(userId, slug).guideId;
}

export function useUsersById(userIds: string[] | undefined | null): UserClass[] | undefined {
    const firebaseRefs = useFirebaseRefs();

    const userDocsQuery = useMemo(
        () => (userIds?.length ?? 0) > 0
            ? query(
                collection(firebaseRefs.firestore, 'users'),
                where('uid', 'in', userIds) // TODO this needs to be broken into chunks of 30 or less
            )
            : undefined,
        [ firebaseRefs.firestore, userIds ]
    );
    const userDocs = useMemoizedQuery(userDocsQuery, 'useUsersById');

    return useMemo(
        () => userDocs === undefined ? undefined : userDocs
            .map(firebaseRecordToUserClass)
            .filter(record => !!record) as UserClass[],
        [ userDocs ]
    );
}

export function useTripByIdWithStatus(tripid: string | null | undefined) {
    const firebaseRefs = useFirebaseRefs();

    const docResult = useDoc(
        tripid ? doc(firebaseRefs.firestore, 'trips', tripid).withConverter(tripConverter) : undefined,
        'useTripById'
    );

    // result is undefined both while loading and when the trip is missing / inaccessible;
    // isLoading distinguishes the two (false + undefined result == not found / no access).
    return { trip: docResult.result, isLoading: docResult.isLoading };
}

export function useTripById(tripid: string | null | undefined) {
    return useTripByIdWithStatus(tripid).trip;
}

export function useTripPlacesById(tripid: string | undefined): Place[] | undefined {
    const firebaseRefs = useFirebaseRefs();
    return useCollection(
        tripid ? collection(firebaseRefs.firestore, "trips", tripid, "activities").withConverter(placeConverter) : undefined,
        'useTripPlacesById'
    );
}

export function useGuidePlacesById(guide: Guide | undefined): Place[] | undefined {
    const firebaseRefs = useFirebaseRefs();
    const guideId = guide?.docid ?? undefined;
    const userId = guide?.creatorId ?? undefined;
    return useCollection(
        (guideId && userId) ? collection(firebaseRefs.firestore, "users", userId, "guides", guideId, "guideplaces").withConverter(placeConverter) : undefined,
        'useGuidePlacesById'
    );
}

export function useTripChangeLogsById(tripid: string | undefined): ChangeLog[] | undefined {
    const firebaseRefs = useFirebaseRefs();
    return useCollection(
        tripid ? collection(firebaseRefs.firestore, "trips", tripid, "changes").withConverter(changeLogConverter) : undefined,
        'useTripChangeLogsById'
    );
}

export function useSavedPlaceById(userId: string, placeDocId: string): Place | undefined {
    const firebaseRefs = useFirebaseRefs();
    const { uidPlaces } = useContext(HomeContext);

    const placeFromCache = useMemo(
        () => uidPlaces.find(p => p.docid === placeDocId),
        [ placeDocId, uidPlaces ]
    );

    const placeFromDatabase = useDoc(
        !placeFromCache && userId && placeDocId
            ? doc(firebaseRefs.firestore, 'users', userId, 'places', placeDocId).withConverter(placeConverter)
            : undefined,
        'useSavedPlaceById'
    );

    return placeFromCache ?? placeFromDatabase?.result;
}

export function useGuideByIdWithStatus(userId: string | undefined, guideDocId: string | undefined) {
    const firebaseRefs = useFirebaseRefs();
    const { uidGuides } = useContext(HomeContext);

    const guideFromCache = useMemo(
        () => uidGuides ? uidGuides.find(p => p.docid === guideDocId) : undefined,
        [ guideDocId, uidGuides ]
    );

    const queryDb = !guideFromCache && !!userId && !!guideDocId;
    const guideFromDatabase = useDoc(
        queryDb
            ? doc(firebaseRefs.firestore, 'users', userId!, 'guides', guideDocId!).withConverter(guideConverter)
            : undefined,
        'useGuideById'
    );

    const guide = guideFromCache ?? guideFromDatabase?.result;
    // Loading only while we're actually waiting on the DB (cache miss + a real query in flight).
    // When inputs are missing or the doc resolved absent, we're not loading — just empty.
    const isLoading = queryDb && guideFromDatabase.isLoading;
    return { guide, isLoading };
}

export function useGuideById(userId: string | undefined, guideDocId: string | undefined): Guide | undefined {
    return useGuideByIdWithStatus(userId, guideDocId).guide;
}

export function useGuideByUsernameAndSlugWithStatus(username: string | undefined, slug: string | undefined):
    { guide: Guide | undefined, userId: string | undefined, isLoading: boolean, notFound: boolean } {
    const { userId, isLoading: userLoading } = useUserIdByUsernameWithStatus(username);
    const { guideId, isLoading: guideIdLoading } = useGuideIdByUserIdAndSlugWithStatus(userId, slug);
    const { guide, isLoading: guideLoading } = useGuideByIdWithStatus(userId, guideId);

    // Not found if any link in the lookup chain resolved to "absent" (vs. still loading).
    const notFound = (!!username && !userLoading && !userId)
        || (!!userId && !!slug && !guideIdLoading && !guideId)
        || (!!userId && !!guideId && !guideLoading && !guide);
    const isLoading = !guide && !notFound;
    return { guide, userId, isLoading, notFound };
}

export function useGuideByUsernameAndSlug(username: string | undefined, slug: string | undefined): Guide | undefined {
    return useGuideByUsernameAndSlugWithStatus(username, slug).guide;
}

export function useGuideSlugByGuideId(userId: string | undefined, guideId: string | null | undefined): string | null {
    const firebaseRefs = useFirebaseRefs();

    const slugQuery = useMemo(
        () => userId && guideId
            ? query(
                collection(firebaseRefs.firestore, 'users', userId, 'guideSlugs'),
                where('guideId', '==', guideId)
            ).withConverter(guideSlugClassConverter)
            : undefined,
        [ firebaseRefs.firestore, userId, guideId ]
    );

    const results = useMemoizedQuery(slugQuery, 'useGuideSlugByGuideId');

    return useMemo(
        () => {
            if (!results || results.length === 0) return null;
            if (results.length === 1) return results[0].guideSlug;

            // Multiple slugs for the same guideId — pick the most recently created one.
            // This can happen if delete + insert in AddEditGuide races or partially fails.
            // Performed client side, minimal duplicate slug docs and avoids slug database indexes for each user.
            const sorted = [...results].sort((a, b) => {
                const aTime = a.createdAt?.toMillis?.() ?? 0;
                const bTime = b.createdAt?.toMillis?.() ?? 0;
                return bTime - aTime; // descending
            });
            return sorted[0].guideSlug;
        },
        [ results ]
    );
}

export function useTripActivityByIdWithStatus(tripDocId: string | undefined, placeDocId: string | undefined) {
    const firebaseRefs = useFirebaseRefs();

    const placeResult = useDoc(
        tripDocId && placeDocId
            ? doc(firebaseRefs.firestore, 'trips', tripDocId, 'activities', placeDocId).withConverter(placeConverter)
            : undefined,
        'useTripActivityById'
    );

    return { place: placeResult.result, isLoading: placeResult.isLoading };
}

export function useTripActivityById(tripDocId: string | undefined, placeDocId: string | undefined): Place | undefined {
    return useTripActivityByIdWithStatus(tripDocId, placeDocId).place;
}

export function useHomeDataContextState(userData: UserData): HomeData {
    const firebaseRefs = useFirebaseRefs();
    const [ tripAdjacentUsersById, setTripAdjacentUsersById ] = useState<Map<string, UserClass>>(new Map());

    const uidPlaces = useCollection(
        userData.isLoggedIn && userData.firebaseUserId
            ? collection(firebaseRefs.firestore, "users", userData.firebaseUserId, "places").withConverter(placeConverter)
            : undefined,
        'useHomeData:user-places',
    );

    const uidGuides = useCollection(
        userData.isLoggedIn && userData.firebaseUserId
            ? collection(firebaseRefs.firestore, "users", userData.firebaseUserId, "guides").withConverter(guideConverter)
            : undefined,
        'useHomeData:user-guides',
    );

    const memoizedQuery = useMemo(
        () => userData.status !== 'logged-out' && userData.firebaseUserId
            ? query(
                collection(firebaseRefs.firestore, "trips"),
                where('users', 'array-contains', userData.firebaseUserId)
            ).withConverter(tripConverter)
            : undefined,
        [ firebaseRefs.firestore, userData.firebaseUserId, userData.status ]
    );
    const unprocessedUserTrips = useMemoizedQuery(
        memoizedQuery,
        'useHomeData:user-trips'
    );
    const { userTrips, userTripsById } = useMemo(
        () => {
            const trips = unprocessedUserTrips ? filterAndSortTrips(unprocessedUserTrips) : undefined;
            if (trips) {
                const byId = new Map<string, Trip>();
                for (const trip of trips) {
                    if (trip.docid) {
                        byId.set(trip.docid, trip);
                    }
                }
                return { userTrips: trips, userTripsById: byId };
            } else {
                return { userTrips: trips, userTripsById: undefined };
            }
        },
        [ unprocessedUserTrips ]
    );

    //tripAdjacentUsers & currentUser
    useEffect(() => {
        if (userTrips !== undefined) {
            const tripAdjacentUserIds = new Set<string>();
            userTrips.forEach(trip => {
                if (Array.isArray(trip.users)) {
                    trip.users.forEach(userId => tripAdjacentUserIds.add(userId));
                } else if (trip.users) {
                    // We have some buggy trips whose users are dicts, not arrays
                    Object.values(trip.users).forEach(value => {
                        if (typeof value === 'string') {
                            tripAdjacentUserIds.add(value);
                        }
                    });
                }
            });

            const tripAdjacentUserclassPromises = Array.from(tripAdjacentUserIds)
                .map(async (uid) => {
                    const tripAdjacentUserclassRef = doc(firebaseRefs.firestore, "users", uid);
                    const tripAdjacentUserclassSnapshot = await getDoc(tripAdjacentUserclassRef); // TODO use executeQueriesAgainstCacheThenServer() here
                    return firebaseRecordToUserClass(tripAdjacentUserclassSnapshot.data());
                });
            Promise.all(tripAdjacentUserclassPromises).then((tripAdjacentUserclass) => {
                const usersById = new Map<string, UserClass>();
                tripAdjacentUserclass.forEach(user => {
                    // filter out the current user and any records that couldn't be loaded
                    if (user && user.uid && user.uid !== userData.firebaseUserId) {
                        usersById.set(user.uid, user);
                    }
                });
                const newAdjacentUsers: UserClass[] = [];
                usersById.forEach(value => {
                    newAdjacentUsers.push(value);
                });
                setTripAdjacentUsersById(usersById);
            });
        } else {
            setTripAdjacentUsersById(new Map());
        }
    }, [ firebaseRefs.firestore, userData.firebaseUserId, userTrips ]);

    return {
        uidPlaces: uidPlaces ?? [],
        uidGuides: uidGuides,
        tripAdjacentUsersById,
        userTrips,
        userTripsById,
    };
}

export function useTripAdjacentUserFavorites(tripAdjacentUsersById: Map<string, UserClass>){
    const firebaseRefs = useFirebaseRefs();
    const [ tripAdjacentUserFavorites, setTripAdjacentUserFavorites ] = useState<Place[] | undefined>(undefined);

    useEffect(() => {
        if (tripAdjacentUsersById.size > 0) {
            const chunkedUsers = splitArray([ ...tripAdjacentUsersById.keys() ], 15);
            const queries = chunkedUsers.map((chunk) => query(
                collectionGroup(firebaseRefs.firestore, 'places'),
                and(
                    where('userid', 'in', chunk),
                    or(
                        where('categories', 'array-contains', 'Favorite'),
                        where('category', '==', 'Favorite')
                    )
                )).withConverter(placeConverter));
            executeQueriesAgainstCacheThenServer(queries,
                snapshot => snapshot
                    ? snapshot.docs.map(d => d.data())
                    : [],
                results => setTripAdjacentUserFavorites(results.flatMap((fav) => fav)));
        }
    }, [ tripAdjacentUsersById, firebaseRefs ]);

    return tripAdjacentUserFavorites;
}

function filterAndSortTrips(trips: Trip[]) {
    return trips
        .filter(trip => trip.lat !== null && trip.lat !== 0) // TODO 0-lat is valid! Change our filters and data accordingly
        .sort((a, b) => {
            if (a.startdate && b.startdate) {
                return b.startdate.compareTo(a.startdate);
            } else if (a.startdate) {
                return -1;
            } else if (b.startdate) {
                return 1;
            } else {
                return 0;
            }
        });
}

function readSafeArea() {
    if (typeof window === 'undefined') return { top: 0, bottom: 0 };
    const styles = getComputedStyle(document.documentElement);
    const top = parseInt(styles.getPropertyValue("--tscroll-safe-area-inset-top"), 10) || 0;
    const bottom = parseInt(styles.getPropertyValue("--tscroll-safe-area-inset-bottom"), 10) || 0;
    return { top, bottom };
}

export function useSafeArea() {
    const [safeArea, setSafeArea] = useState(readSafeArea);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const read = () => setSafeArea(readSafeArea());

        // Re-read in case values changed between SSR and hydration
        read();

        window.addEventListener("resize", read);
        window.addEventListener("orientationchange", read);

        return () => {
            window.removeEventListener("resize", read);
            window.removeEventListener("orientationchange", read);
        };
    }, []);

    return { safeAreaInsetTop: safeArea.top, safeAreaInsetBottom: safeArea.bottom };
}

export function useWindowHeight() {
    const [ height, setHeight ] = useState<number>(() => window.innerHeight);

    useEffect(() => {
        const listener = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener('resize', listener);
        return () => window.removeEventListener('resize', listener);
    }, []);

    return useMemo(() => ({ windowHeight: height }), [ height ]);
}

export function useIsWide(): boolean | null {
    const [isWide, setIsWide] = useState<boolean | null>(null);
    useEffect(() => {
        const check = () => setIsWide(window.innerWidth >= 768);
        check();
        window.addEventListener('resize', check);
        window.addEventListener('orientationchange', check);
        return () => {
            window.removeEventListener('resize', check);
            window.removeEventListener('orientationchange', check);
        };
    }, []);
    return isWide;
}

export function usePromiseValue<T>(promiseProvider: () => Promise<T | undefined>): T | undefined {
    const [ value, setValue ] = useState<T>();

    useEffect(() => {
        if (promiseProvider) {
            promiseProvider().then(setValue);
        } else {
            setValue(undefined);
        }
    }, [ promiseProvider ]);

    return value;
}
